/**
 * WeatherWeekPresenter - 5-day weather forecast view presenter
 *
 * NOTE: This presenter component is separated from WeatherWeeklyWidget.tsx for Module Federation.
 * It contains the same UI logic but receives all data via props instead of MobX injection.
 * This allows JHP to consume it via Module Federation while Zoomin's WeatherWeeklyWidget.tsx
 * continues to use MobX stores for data. Both components should render identically.
 */

import * as React from 'react'

import { classList } from 'react-classlist-helper'

import OpenWeatherIcon from '~/client/src/shared/components/OpenWeatherIcon'
import WeatherBarPresenter, {
  IWeatherBarForecast,
} from '~/client/src/shared/components/WeatherHourlyWidget/WeatherBarPresenter'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import CityAndDatePresenter, { IProjectAddress } from './CityAndDatePresenter'

import './WeatherWeekPresenter.scss'

const MAX_BAR_HEIGHT = 266
const MAX_BAR_HEIGHT_COMPACT = 170
const MIN_BAR_HEIGHT = 135
const MIN_BAR_HEIGHT_COMPACT = 125

export interface IWeatherWeekForecast extends IWeatherBarForecast {
  /** Weather description key (e.g., "clear sky") */
  weatherDescription: string
}

export interface IWeatherWeekPresenterProps {
  /** Daily forecasts for the week (should be 5 days, excluding today) */
  forecasts: IWeatherWeekForecast[]
  /** Project address for display */
  projectAddress: IProjectAddress
  /** Display units */
  units: 'imperial' | 'metric'
  /** Formatted date range string (e.g., "Dec 16 - Dec 20, 2024") */
  formattedDateRange: string
  /** Format weekday for display (e.g., "Mon") */
  formatWeekday?: (timestamp: number) => string
  /** Format month and day for display (e.g., "Dec 16") */
  formatMonthDay?: (timestamp: number) => string
  /** Icon size in pixels */
  iconSize?: number
  /** Additional CSS class name */
  className?: string
  /** Whether it's constrained horizontally */
  compactH?: boolean
  /** Whether it's constrained vertically */
  compactV?: boolean
}

interface MostFrequentWeather {
  icon: string
  description: string
}

/**
 * Get temperature unit label
 */
function temperatureUnits(units: 'imperial' | 'metric'): string {
  return units === 'imperial' ? '°F' : '°C'
}

export default function WeatherWeekPresenter({
  forecasts,
  projectAddress,
  units,
  formattedDateRange,
  formatWeekday,
  formatMonthDay,
  iconSize = 32,
  className,
  compactH,
  compactV,
}: IWeatherWeekPresenterProps): JSX.Element | null {
  if (!forecasts || forecasts.length === 0) {
    return null
  }

  const tempUnit = temperatureUnits(units)

  // Calculate temperature range for bar heights
  const maxBarHeight = compactV ? MAX_BAR_HEIGHT_COMPACT : MAX_BAR_HEIGHT
  const minBarHeight = compactV ? MIN_BAR_HEIGHT_COMPACT : MIN_BAR_HEIGHT
  const minTemp = Math.min(...forecasts.map(item => item.temperature))
  const maxTemp = Math.max(...forecasts.map(item => item.temperature))
  const delta =
    maxTemp !== minTemp
      ? (maxBarHeight - minBarHeight) / (maxTemp - minTemp)
      : 0

  // Get most frequent weather for summary
  const { icon, description } = getMostFrequent(forecasts)

  // Get weather description translation
  const weatherDescription =
    Localization.translator.weatherDescriptions?.[description] ?? description

  return (
    <div
      className={classList({
        'col weather-day-widget full-width justify-around': true,
        'gap-16': !compactV,
        'gap-4': compactV,
        [className || '']: !!className,
      })}
    >
      <div
        className={classList({
          'x-around y-start flex-grow-0': true,
          'row gap-16': !compactH,
          'col gap-4': compactH,
        })}
      >
        <div
          className={classList({
            col: true,
            'gap-8': !compactH && !compactV,
            'gap-2': compactH || compactV,
          })}
        >
          <div className="row y-start gap-4 no-grow">
            <OpenWeatherIcon
              openWeatherIconCode={icon}
              isColor
              className="icon-w24 icon-h24 no-grow w-fit-content"
            />
            <div
              className={classList({
                col: true,
                'gap-8': !compactH && !compactV,
                'gap-2': compactH || compactV,
                'as-stretch flex-grow': true,
              })}
            >
              <div
                className={classList({
                  'weather-grid-container': true,
                  'gap-8': !compactH && !compactV,
                  'gap-2': compactH || compactV,
                })}
              >
                <span className="text grey-light line-24">
                  {Localization.translator.temperatureRange?.avg}
                </span>
                <span className="text bold extra-large line-24 lp1 capitalize">
                  {weatherDescription}
                </span>

                <span className="text grey-light">
                  {Localization.translator.temperatureRange?.high}
                </span>
                <span className="text grey-10 bold">
                  {maxTemp}
                  {tempUnit}
                </span>
                <span className="grey-light text">
                  {Localization.translator.temperatureRange?.low}
                </span>
                <span className="text grey-10 bold">
                  {minTemp}
                  {tempUnit}
                </span>
              </div>
            </div>
          </div>
        </div>

        {projectAddress?.city && projectAddress?.state && (
          <CityAndDatePresenter
            projectAddress={projectAddress}
            date={formattedDateRange}
            compactH={compactH}
            compactV={compactV}
          />
        )}
      </div>

      <div className="row y-end gap-16 no-select weather-daily-widget">
        {forecasts.map((forecast, i) => {
          const height =
            maxTemp !== minTemp
              ? Math.round(
                  maxBarHeight - delta * (maxTemp - forecast.temperature),
                )
              : maxBarHeight

          // Get the timestamp (should be in `date` field for daily forecasts)
          const timestamp = forecast.date ?? forecast.time ?? 0

          return (
            <WeatherBarPresenter
              key={i}
              forecast={forecast}
              units={units}
              height={height}
              iconSize={iconSize}
              barWidthClass="w72"
              renderTimeSection={() => (
                <>
                  <span
                    className="text bold"
                    title={new Date(timestamp).toString()}
                  >
                    {formatWeekday ? formatWeekday(timestamp) : '--'}
                  </span>
                  <span className="text small grey-light lp05">
                    {formatMonthDay ? formatMonthDay(timestamp) : '--'}
                  </span>
                </>
              )}
            />
          )
        })}
      </div>
    </div>
  )
}

/**
 * Get the most frequent weather icon and description from forecasts
 */
function getMostFrequent(
  forecasts: IWeatherWeekForecast[],
): MostFrequentWeather {
  const forecastCounts = forecasts.reduce(
    (acc, curr) => {
      // Force day icon for summary
      const currentIconCode = curr.weatherIconCode.substring(0, 2) + 'd'
      acc.icons[currentIconCode] = (acc.icons[currentIconCode] || 0) + 1
      acc.descriptions[curr.weatherDescription] =
        (acc.descriptions[curr.weatherDescription] || 0) + 1
      return acc
    },
    {
      icons: {} as Record<string, number>,
      descriptions: {} as Record<string, number>,
    },
  )

  const mostFrequentIcon = Object.entries(forecastCounts.icons).reduce(
    (a, b) => (a[1] > b[1] ? a : b),
    ['01d', 0],
  )[0]

  const mostFrequentDescription = Object.entries(
    forecastCounts.descriptions,
  ).reduce((a, b) => (a[1] > b[1] ? a : b), ['clear sky', 0])[0]

  return { icon: mostFrequentIcon, description: mostFrequentDescription }
}
